/*
 * Wilson dataLayer interaction tracking (clicks, impressions, video).
 * Moved out of snippets/data-layer.liquid so it no longer runs inline at the top of <head>.
 * Loaded with `defer`: it still runs before DOMContentLoaded, so every block's init path is unchanged.
 * Depends on window.pushWilsonDataLayer from snippets/data-layer.liquid.
 */
/*
  Recommended products impression tracking.
  Fires once per recommendation block per page view when the block becomes visible.
 */
  (function () {
    if (typeof window.IntersectionObserver !== 'function') return;
    if (!(document.documentElement instanceof Element)) return;

    var recommendationRootSelector =
      '[data-recommended-products-slider], product-recommendations, .algolia-recommendations-root';
    var observedRoots = new WeakSet();
    var visibleRoots = new WeakSet();
    var emittedRoots = new WeakSet();

    function getTextContent(element) {
      if (!(element instanceof Element)) return '';
      return (element.textContent || '').trim();
    }

    function getFirstValue(values) {
      for (var index = 0; index < values.length; index += 1) {
        var value = values[index];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }

      return '';
    }

    function getHeadingLevelValue(element) {
      if (!(element instanceof Element)) return '';

      var ariaLevel = element.getAttribute('aria-level');
      var parsed = Number(ariaLevel);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > 6) return '';

      return String(parsed);
    }

    function getNodeOrder(node, relativeTo) {
      if (!(node instanceof Element) || !(relativeTo instanceof Element)) return -1;

      if (node === relativeTo) return 0;

      var position = node.compareDocumentPosition(relativeTo);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    }

    function findBestPreviousHeading(sectionRoot, recommendationRoot) {
      if (!(sectionRoot instanceof Element) || !(recommendationRoot instanceof Element)) return '';

      var headingCandidates = sectionRoot.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"][aria-level]');
      var bestText = '';
      var bestDistance = -1;
      var bestLevel = 7;

      for (var index = 0; index < headingCandidates.length; index += 1) {
        var candidate = headingCandidates[index];
        if (!(candidate instanceof Element)) continue;

        if (getNodeOrder(candidate, recommendationRoot) !== -1) continue;

        var candidateText = getTextContent(candidate);
        if (!candidateText) continue;

        var levelText = candidate.tagName.toLowerCase().replace('h', '');
        var candidateLevel = Number(levelText);
        if (!Number.isFinite(candidateLevel)) {
          candidateLevel = Number(getHeadingLevelValue(candidate));
        }
        if (!Number.isFinite(candidateLevel) || candidateLevel < 1 || candidateLevel > 6) {
          candidateLevel = 6;
        }

        var position = candidate.compareDocumentPosition(recommendationRoot);
        var distanceBoost = (position & Node.DOCUMENT_POSITION_CONTAINED_BY) ? 1000000 : index;

        if (
          distanceBoost > bestDistance ||
          (distanceBoost === bestDistance && candidateLevel < bestLevel)
        ) {
          bestDistance = distanceBoost;
          bestLevel = candidateLevel;
          bestText = candidateText;
        }
      }

      return bestText;
    }

    function getSectionHeading(root) {
      if (!(root instanceof Element)) return '';

      var sectionRoot = root.closest('.shopify-section, [id^="shopify-section-"], section, main');

      return getFirstValue([
        root.getAttribute('aria-label'),
        root.getAttribute('data-section-heading'),
        getTextContent(root.querySelector('.recommended-products__heading')),
        sectionRoot instanceof Element
          ? getTextContent(sectionRoot.querySelector('.section-resource-list__content h1'))
          : '',
        sectionRoot instanceof Element
          ? getTextContent(sectionRoot.querySelector('.section-resource-list__content h2'))
          : '',
        sectionRoot instanceof Element
          ? getTextContent(sectionRoot.querySelector('.section-resource-list__content h3'))
          : '',
        getTextContent(root.querySelector('.algolia-recommendations__eyebrow')),
        getTextContent(root.querySelector('.algolia-recommendations__intro h1')),
        getTextContent(root.querySelector('.algolia-recommendations__intro h2')),
        getTextContent(root.querySelector('.algolia-recommendations__intro h3')),
        sectionRoot instanceof Element ? findBestPreviousHeading(sectionRoot, root) : ''
      ]);
    }

    function addUniqueProductId(list, seenIds, rawValue) {
      if (typeof rawValue !== 'string') return;

      var value = rawValue.trim();
      if (!value || seenIds.has(value)) return;

      seenIds.add(value);
      list.push(value);
    }

    function getRecommendedProductIds(root) {
      if (!(root instanceof Element)) return [];

      var ids = [];
      var seenIds = new Set();

      var linkNodes = root.querySelectorAll('a[data-product-id]');
      for (var index = 0; index < linkNodes.length; index += 1) {
        addUniqueProductId(ids, seenIds, linkNodes[index].getAttribute('data-product-id'));
      }

      var productCards = root.querySelectorAll('product-card[data-product-id]');
      for (var productCardIndex = 0; productCardIndex < productCards.length; productCardIndex += 1) {
        addUniqueProductId(ids, seenIds, productCards[productCardIndex].getAttribute('data-product-id'));
      }

      var customProductCards = root.querySelectorAll('product-card-custom');
      for (var customCardIndex = 0; customCardIndex < customProductCards.length; customCardIndex += 1) {
        var customCard = customProductCards[customCardIndex];
        addUniqueProductId(
          ids,
          seenIds,
          getFirstValue([
            customCard.getAttribute('data-product-id'),
            customCard.getAttribute('data-selected-variant-id')
          ])
        );
      }

      return ids;
    }

    function findClosestRecommendationRoot(node) {
      if (!(node instanceof Element)) return null;
      if (node.matches(recommendationRootSelector)) return node;
      return node.closest(recommendationRootSelector);
    }

    function emitRecommendationView(root) {
      if (!(root instanceof Element)) return;
      if (!visibleRoots.has(root) || emittedRoots.has(root)) return;

      var productIds = getRecommendedProductIds(root);
      if (!productIds.length) return;

      window.pushWilsonDataLayer({
        event: 'recommended_products_viewed',
        section_heading: getSectionHeading(root),
        recommended_products: productIds.join(','),
      });

      emittedRoots.add(root);
      intersectionObserver.unobserve(root);
    }

    function observeRecommendationRoot(root) {
      if (!(root instanceof Element) || observedRoots.has(root)) return;
      observedRoots.add(root);
      intersectionObserver.observe(root);
    }

    function scanRecommendationRoots(scope) {
      if (!(scope instanceof Element) && scope !== document) return;

      if (scope instanceof Element && scope.matches(recommendationRootSelector)) {
        observeRecommendationRoot(scope);
      }

      var roots =
        scope === document
          ? document.querySelectorAll(recommendationRootSelector)
          : scope.querySelectorAll(recommendationRootSelector);

      for (var index = 0; index < roots.length; index += 1) {
        observeRecommendationRoot(roots[index]);
      }
    }

    var intersectionObserver = new IntersectionObserver(function (entries) {
      for (var index = 0; index < entries.length; index += 1) {
        var entry = entries[index];
        var root = entry.target;

        if (entry.isIntersecting) {
          visibleRoots.add(root);
          emitRecommendationView(root);
        } else {
          visibleRoots.delete(root);
        }
      }
    });

    var mutationObserver = new MutationObserver(function (mutations) {
      for (var mutationIndex = 0; mutationIndex < mutations.length; mutationIndex += 1) {
        var mutation = mutations[mutationIndex];
        var targetRoot = findClosestRecommendationRoot(mutation.target);
        if (targetRoot && visibleRoots.has(targetRoot)) {
          emitRecommendationView(targetRoot);
        }

        for (var nodeIndex = 0; nodeIndex < mutation.addedNodes.length; nodeIndex += 1) {
          var addedNode = mutation.addedNodes[nodeIndex];
          if (!(addedNode instanceof Element)) continue;

          scanRecommendationRoots(addedNode);

          var addedNodeRoot = findClosestRecommendationRoot(addedNode);
          if (addedNodeRoot && visibleRoots.has(addedNodeRoot)) {
            emitRecommendationView(addedNodeRoot);
          }
        }
      }
    });

    function initRecommendationViewTracking() {
      scanRecommendationRoots(document);
      mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initRecommendationViewTracking);
    } else {
      initRecommendationViewTracking();
    }
  })();

/*
  PDP accordion section impressions.
  Fires when a details accordion is opened in PDP main content.
 */
  (function () {
    var pendingAccordionInteraction = new WeakSet();

    function getTextContent(element) {
      if (!(element instanceof Element)) return '';
      return (element.textContent || '').trim();
    }

    function getAccordionLabel(details) {
      if (!(details instanceof HTMLDetailsElement)) return '';

      var summary = details.querySelector('summary');
      if (!(summary instanceof HTMLElement)) return '';

      var heading = summary.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"]');

      var labelCandidates = [
        summary.getAttribute('data-analytics-label'),
        summary.getAttribute('aria-label'),
        heading ? getTextContent(heading) : '',
        getTextContent(summary)
      ];

      for (var index = 0; index < labelCandidates.length; index += 1) {
        var label = labelCandidates[index];
        if (typeof label === 'string' && label.trim().length > 0) {
          return label.trim().replace(/\s+/g, ' ');
        }
      }

      return '';
    }

    function isPdpAccordion(details) {
      if (!(details instanceof HTMLDetailsElement)) return false;
      if (!details.open) return false;
      if (!details.closest('accordion-custom')) return false;

      var mainContent = details.closest('#MainContent');
      if (!(mainContent instanceof HTMLElement)) return false;

      var template = mainContent.getAttribute('data-template') || '';
      return template.indexOf('product') !== -1;
    }

    document.addEventListener(
      'click',
      function (event) {
        var summary = event.target instanceof Element ? event.target.closest('summary') : null;
        if (!(summary instanceof HTMLElement)) return;

        var details = summary.closest('details');
        if (!(details instanceof HTMLDetailsElement)) return;

        pendingAccordionInteraction.add(details);
      },
      true
    );

    document.addEventListener(
      'toggle',
      function (event) {
        var details = event.target;
        if (!(details instanceof HTMLDetailsElement)) return;

        var openedFromUserInteraction = pendingAccordionInteraction.has(details);
        pendingAccordionInteraction.delete(details);
        if (!openedFromUserInteraction) return;

        if (!isPdpAccordion(details)) return;

        var accordionLabel = getAccordionLabel(details);
        if (!accordionLabel) return;

        window.dataLayer = window.dataLayer || [];
        window.pushWilsonDataLayer({
          event: 'pdp_section_impression',
          accordion_label: accordionLabel,
        });
      },
      true
    );
  })();

/*
  Footer menu link clicks. menu_category comes from the data-menu-category
  attribute set on the <nav> in blocks/menu.liquid (the block's heading).
 */
  document.addEventListener('click', function (event) {
    const link = event.target.closest('footer .menu__item a');
    if (!link) return;

    const menuContainer = link.closest('[data-menu-category]');

    window.pushWilsonDataLayer({
      event: 'footer_menu_click',
      click_text: link.textContent.trim(),
      click_url: link.href,
      menu_category: menuContainer ? menuContainer.getAttribute('data-menu-category') : '',
    });
  });

/*
  Footer "Need Help?" (Chat Online / Contact Us) and newsletter signup button clicks
 */
  document.addEventListener('click', function (event) {
    const link = event.target.closest(
      'footer .footer-need-help a, footer a[href$="/pages/newsletter"]'
    );
    if (!link) return;

    const needHelpContainer = link.closest('.footer-need-help');
    const groupContainer = link.closest('.group-block-content');
    const heading = needHelpContainer
      ? needHelpContainer.querySelector('.footer-need-help__heading')
      : groupContainer
      ? groupContainer.querySelector('h1, h2, h3, h4, h5, h6')
      : null;

    window.pushWilsonDataLayer({
      event: 'footer_menu_click',
      click_text: link.textContent.trim(),
      click_url: link.href,
      menu_category: heading ? heading.textContent.trim() : '',
    });
  });

/*
  Recommended product tile clicks across recommendation blocks.
  Applies to native recommendations, "Recommended products", and Algolia recommendation blocks.
 */
  (function () {
    function getTextContent(element) {
      if (!(element instanceof Element)) return '';
      return (element.textContent || '').trim();
    }

    function getFirstValue(values) {
      for (var index = 0; index < values.length; index += 1) {
        var value = values[index];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }

      return '';
    }

    function getSectionHeading(recommendationRoot) {
      if (!(recommendationRoot instanceof Element)) return '';

      return getFirstValue([
        recommendationRoot.getAttribute('aria-label'),
        getTextContent(recommendationRoot.querySelector('.recommended-products__heading')),
        getTextContent(recommendationRoot.querySelector('.section-resource-list__content h1')),
        getTextContent(recommendationRoot.querySelector('.section-resource-list__content h2')),
        getTextContent(recommendationRoot.querySelector('.section-resource-list__content h3')),
        getTextContent(recommendationRoot.querySelector('.algolia-recommendations__eyebrow')),
        getTextContent(recommendationRoot.querySelector('.algolia-recommendations__intro h1')),
        getTextContent(recommendationRoot.querySelector('.algolia-recommendations__intro h2')),
        getTextContent(recommendationRoot.querySelector('.algolia-recommendations__intro h3')),
      ]);
    }

    document.addEventListener('click', function (event) {
      var link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link) return;

      var recommendationRoot = link.closest(
        '[data-recommended-products-slider], product-recommendations, .algolia-recommendations-root, .algolia-recommendations'
      );
      if (!recommendationRoot) return;

      var isRecommendationTileLink = Boolean(
        link.closest('.recommended-products__card') ||
          link.classList.contains('product-card__link') ||
          link.classList.contains('c-product-tile-decorator__content-wrapper') ||
          link.closest('product-card') ||
          link.closest('product-card-custom')
      );
      if (!isRecommendationTileLink) return;

      var tileContainer =
        link.closest('.recommended-products__card, .resource-list__item, .mcs-column, product-card, product-card-custom') || link;
      var productCard = link.closest('product-card');
      var productCardCustom = link.closest('product-card-custom');

      var sectionHeading = getSectionHeading(recommendationRoot);
      var productName = getFirstValue([
        link.getAttribute('data-product-name'),
        link.getAttribute('aria-label'),
        getTextContent(tileContainer.querySelector('.recommended-products__title')),
        getTextContent(tileContainer.querySelector('[data-replaceable-card-title]')),
        getTextContent(link.querySelector('.visually-hidden')),
        getTextContent(link),
      ]);
      var productId = getFirstValue([
        link.getAttribute('data-product-id'),
        productCardCustom ? productCardCustom.getAttribute('data-selected-variant-id') : '',
        productCardCustom ? productCardCustom.getAttribute('data-product-id') : '',
        productCard ? productCard.getAttribute('data-product-id') : '',
      ]);

      window.pushWilsonDataLayer({
        event: 'recommended_products_click',
        section_heading: sectionHeading,
        product_name: productName,
        product_id: productId,
      });
    });
  })();

/*
  Video tracking for interactive Shopify and YouTube videos.
  Tracks start (0%), halfway progress (50%), and completion (100%).
 */
  (function () {
    var nativeVideoStates = new WeakMap();
    var youtubeFrames = new WeakSet();
    var youtubeStates = new WeakMap();
    var youtubeApiPromise = null;

    function pushVideoEvent(eventName, provider, title, percent, url) {
      window.pushWilsonDataLayer({
        event: eventName,
        video_provider: provider,
        video_title: title,
        video_percent: percent,
        video_url: url,
      });
    }

    function getVideoTitle(element) {
      if (!(element instanceof Element)) return 'Video';

      var titleContainer = element.closest('[data-video-title]');
      var containerTitle = titleContainer ? titleContainer.getAttribute('data-video-title') : null;
      if (typeof containerTitle === 'string' && containerTitle.trim().length > 0) {
        return containerTitle.trim();
      }

      var titleAttribute = element.getAttribute('title');
      if (typeof titleAttribute === 'string' && titleAttribute.trim().length > 0) {
        return titleAttribute.trim();
      }

      return 'Video';
    }

    function bindNativeVideo(video) {
      if (!(video instanceof HTMLVideoElement)) return;
      if (nativeVideoStates.has(video)) return;

      nativeVideoStates.set(video, {
        started: false,
        progressed: false,
        completed: false
      });

      video.addEventListener('play', function () {
        var state = nativeVideoStates.get(video);
        if (!state || state.started) return;

        state.started = true;
        pushVideoEvent('video_start', 'shopify', getVideoTitle(video), 0, video.currentSrc || video.src || window.location.href);
      });

      video.addEventListener('timeupdate', function () {
        var state = nativeVideoStates.get(video);
        if (!state || state.progressed) return;
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;

        if (video.currentTime / video.duration >= 0.5) {
          state.progressed = true;
          pushVideoEvent('video_progress', 'shopify', getVideoTitle(video), 50, video.currentSrc || video.src || window.location.href);
        }
      });

      video.addEventListener('ended', function () {
        var state = nativeVideoStates.get(video);
        if (!state || state.completed) return;

        state.completed = true;
        pushVideoEvent('video_complete', 'shopify', getVideoTitle(video), 100, video.currentSrc || video.src || window.location.href);
      });
    }

    function ensureYoutubeApi() {
      if (window.YT && typeof window.YT.Player === 'function') {
        return Promise.resolve(window.YT);
      }

      if (youtubeApiPromise) return youtubeApiPromise;

      youtubeApiPromise = new Promise(function (resolve) {
        var previousReadyCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = function () {
          if (typeof previousReadyCallback === 'function') {
            previousReadyCallback();
          }
          resolve(window.YT);
        };

        var apiScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
        if (!apiScript) {
          apiScript = document.createElement('script');
          apiScript.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(apiScript);
        }
      });

      return youtubeApiPromise;
    }

    function startYoutubeProgressPolling(iframe, player) {
      var state = youtubeStates.get(iframe);
      if (!state || state.intervalId) return;

      state.intervalId = window.setInterval(function () {
        if (state.progressed) return;

        var duration = player.getDuration();
        if (!Number.isFinite(duration) || duration <= 0) return;

        var currentTime = player.getCurrentTime();
        if (!Number.isFinite(currentTime)) return;

        if (currentTime / duration >= 0.5) {
          state.progressed = true;
          pushVideoEvent('video_progress', 'youtube', getVideoTitle(iframe), 50, iframe.src || window.location.href);
        }
      }, 1000);
    }

    function stopYoutubeProgressPolling(iframe) {
      var state = youtubeStates.get(iframe);
      if (!state || !state.intervalId) return;

      window.clearInterval(state.intervalId);
      state.intervalId = null;
    }

    function bindYoutubeVideo(iframe) {
      if (!(iframe instanceof HTMLIFrameElement)) return;
      if (youtubeFrames.has(iframe)) return;
      youtubeFrames.add(iframe);

      ensureYoutubeApi()
        .then(function (YT) {
          if (!YT || typeof YT.Player !== 'function') return;

          youtubeStates.set(iframe, {
            started: false,
            progressed: false,
            completed: false,
            intervalId: null
          });

          var player = new YT.Player(iframe, {
            events: {
              onStateChange: function (event) {
                var state = youtubeStates.get(iframe);
                if (!state) return;

                if (event.data === YT.PlayerState.PLAYING) {
                  if (!state.started) {
                    state.started = true;
                    pushVideoEvent('video_start', 'youtube', getVideoTitle(iframe), 0, iframe.src || window.location.href);
                  }
                  startYoutubeProgressPolling(iframe, player);
                  return;
                }

                if (event.data === YT.PlayerState.ENDED) {
                  stopYoutubeProgressPolling(iframe);
                  if (state.completed) return;

                  state.completed = true;
                  pushVideoEvent('video_complete', 'youtube', getVideoTitle(iframe), 100, iframe.src || window.location.href);
                  return;
                }

                if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.BUFFERING) {
                  stopYoutubeProgressPolling(iframe);
                }
              }
            }
          });
        })
        .catch(function () {
          // noop
        });
    }

    function isYoutubeIframe(iframe) {
      if (!(iframe instanceof HTMLIFrameElement)) return false;
      var src = iframe.getAttribute('src') || '';
      return src.indexOf('youtube.com/embed/') !== -1 || src.indexOf('youtube-nocookie.com/embed/') !== -1;
    }

    function scanVideos(root) {
      if (root instanceof HTMLVideoElement) {
        bindNativeVideo(root);
      }

      if (isYoutubeIframe(root)) {
        bindYoutubeVideo(root);
      }

      if (!(root instanceof Element)) return;

      var nativeVideos = root.querySelectorAll('video');
      for (var i = 0; i < nativeVideos.length; i += 1) {
        bindNativeVideo(nativeVideos[i]);
      }

      var youtubeIframes = root.querySelectorAll(
        'iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]'
      );
      for (var j = 0; j < youtubeIframes.length; j += 1) {
        bindYoutubeVideo(youtubeIframes[j]);
      }
    }

    function initVideoTracking() {
      scanVideos(document.documentElement);

      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i += 1) {
          var addedNodes = mutations[i].addedNodes;
          for (var j = 0; j < addedNodes.length; j += 1) {
            scanVideos(addedNodes[j]);
          }
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initVideoTracking);
    } else {
      initVideoTracking();
    }
  })();

/*
  Social media icon clicks. social_media_platform comes from the
  data-social-platform attribute set on the popover in blocks/social-links.liquid.
 */
  document.addEventListener('click', function (event) {
    const link = event.target.closest('.social-icons__wrapper .social-link-list a');
    if (!link) return;

    const platformContainer = link.closest('[data-social-platform]');

    window.pushWilsonDataLayer({
      event: 'social_media_click',
      social_media_platform: platformContainer ? platformContainer.getAttribute('data-social-platform') : '',
      click_url: link.href,
    });
  });

/*
  Main nav menu link clicks.
 */
  document.addEventListener('click', function (event) {
    const link = event.target.closest('header a.mega-menu__link, header a.menu-list__link, header a.menu-drawer__menu-item');
    if (!link) return;

    const menuListItem = link.closest('[data-menu-list-item]')?.getAttribute('data-menu-list-item');
    const menuLeftNavItem = link.closest('[data-menu-left-nav-item]')?.getAttribute('data-menu-left-nav-item');
    const menuSubcategory = link.closest('[data-menu-subcategory]')?.getAttribute('data-menu-subcategory')
        ?? link.closest(':has([data-menu-subcategory])')?.querySelector('[data-menu-subcategory]')?.getAttribute('data-menu-subcategory');

    window.pushWilsonDataLayer({
      event: 'main_navigation_click',
      click_text: link.querySelector('.menu-link-text').textContent.trim(),
      click_url: link.href,
      menu_category: menuListItem ?? '',
      menu_subcategory: menuLeftNavItem ?? '',
      menu_subcategory_section: menuSubcategory ?? '',
    });
  });

/*
  Retail store card clicks on the store locator page (template-stores).
  Tracks clicks on the store image and phone number link within a .mcs-column.
  retail_store comes from that column's <h2> (heading itself is not interactive).
 */
  document.addEventListener('click', function (event) {
    const column = event.target.closest('.template-stores .mcs-column');
    if (!column) return;

    const target = event.target.closest('img, a.image-block, a[href^="tel:"]');
    if (!target || !column.contains(target)) return;

    const heading = column.querySelector('h2');

    let clickText = '';
    let clickUrl = '';

    if (target.tagName === 'IMG' || target.classList.contains('image-block')) {
      const img = target.tagName === 'IMG' ? target : target.querySelector('img');
      const imageLink = target.closest('a');
      clickText = (img && img.alt) || (imageLink ? imageLink.getAttribute('aria-label') : '') || '';
      clickUrl = imageLink ? imageLink.href : '';
    } else if (target.tagName === 'A') {
      clickText = target.textContent.trim();
      clickUrl = target.href;
    }

    window.pushWilsonDataLayer({
      event: 'retail_store_select',
      retail_store: heading ? heading.textContent.trim() : '',
      click_text: clickText,
      click_url: clickUrl,
    });
  });

/*
  CTA link clicks in the main body content.
  Fires for anchor clicks inside #MainContent and excludes hash-only/javascript links.
 */
  (function () {
    function getTextContent(element) {
      if (!(element instanceof Element)) return '';
      return (element.textContent || '').trim();
    }

    function getFirstValue(values) {
      for (const value of values) {
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }

      return '';
    }

    function getSectionHeading(link) {
      if (!(link instanceof Element)) return '';

      const sectionRoot = link.closest('.shopify-section, [id^="shopify-section-"], section, main');
      if (!(sectionRoot instanceof Element)) return '';

      const labelledBy = sectionRoot.getAttribute('aria-labelledby');
      let labelledByText = '';

      if (labelledBy) {
        const labelIds = labelledBy.split(/\s+/);
        for (const labelId of labelIds) {
          if (!labelId) continue;
          const labelNode = document.getElementById(labelId);
          labelledByText = getTextContent(labelNode);
          if (labelledByText) break;
        }
      }

      const headingNodes = sectionRoot.querySelectorAll(
        'h1, h2, h3, h4, h5, h6, [role="heading"][aria-level]'
      );
      let nearestPreviousHeading = '';

      for (const headingNode of headingNodes) {
        if (!(headingNode instanceof Element)) continue;

        const headingText = getTextContent(headingNode);
        if (!headingText) continue;

        if (headingNode.contains(link)) return headingText;

        const position = headingNode.compareDocumentPosition(link);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
          nearestPreviousHeading = headingText;
        }
      }

      return getFirstValue([
        sectionRoot.getAttribute('data-section-heading'),
        sectionRoot.getAttribute('aria-label'),
        labelledByText,
        nearestPreviousHeading,
        getTextContent(sectionRoot.querySelector('h1')),
        getTextContent(sectionRoot.querySelector('h2')),
        getTextContent(sectionRoot.querySelector('h3')),
      ]);
    }

    function getClickText(link) {
      if (!(link instanceof Element)) return '';

      const imageNode = link.querySelector('img');
      const imageAltText = imageNode instanceof HTMLImageElement ? imageNode.getAttribute('alt') : '';

      return getFirstValue([
        link.getAttribute('data-analytics-label'),
        link.getAttribute('aria-label'),
        getTextContent(link.querySelector('.visually-hidden')),
        getTextContent(link),
        imageAltText || '',
      ]);
    }

    document.addEventListener('click', function (event) {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link) return;

      const mainContent = document.getElementById('MainContent');
      if (!(mainContent instanceof HTMLElement) || !mainContent.contains(link)) return;

      const rawHref = link.getAttribute('href') || '';
      if (rawHref.startsWith('#')) return;
      if (rawHref.toLowerCase().indexOf('javascript:') === 0) return;

      window.pushWilsonDataLayer({
        event: 'cta_click',
        click_text: getClickText(link),
        click_url: link.href,
        section_heading: getSectionHeading(link),
      });
    });
  })();

/*
  CTA block rendered and impression tracking in the main body content.
  - cta_block_rendered: once per section when a heading-led CTA section is in the DOM
  - cta_block_impression: at most once per CTA block per pageview when that block becomes visible
 */
  (function () {
    const CTA_SELECTOR = 'a[href], button, [role="button"], input[type="submit"], input[type="button"]';
    const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]';
    const GENERIC_CLASS_PATTERN =
      /^(shopify-section|shopify-section-group-.+|section-wrapper|section-background|section|section--.+|spacing-style|gap-style|color-.+|layout-panel-.+|border-style|custom-section-.+|hidden|visually-hidden|sr-only)$/i;
    const SECTION_TYPE_ALIASES = {
      'category-columns': 'Category Grid',
      'category-column': 'Category Grid',
    };

    const renderedSections = new WeakSet();
    const observedSections = new WeakSet();
    const impressedSections = new WeakSet();
    const impressedKeys = new Set();
    const supportsIntersectionObserver = typeof window.IntersectionObserver === 'function';

    function getTextContent(element) {
      if (!(element instanceof Element)) return '';
      return (element.textContent || '').trim().replace(/\s+/g, ' ');
    }

    function getMainContent() {
      const mainContent = document.getElementById('MainContent');
      return mainContent instanceof HTMLElement ? mainContent : null;
    }

    function getTopLevelSections(mainContent) {
      if (!(mainContent instanceof Element)) return [];

      const sections = mainContent.querySelectorAll('.shopify-section');
      const topLevel = [];

      for (const section of sections) {
        const parentSection = section.parentElement ? section.parentElement.closest('.shopify-section') : null;
        if (parentSection) continue;
        topLevel.push(section);
      }

      return topLevel;
    }

    function getSectionHeading(sectionRoot) {
      if (!(sectionRoot instanceof Element)) return '';

      const dataHeading = sectionRoot.getAttribute('data-section-heading');
      if (typeof dataHeading === 'string' && dataHeading.trim().length > 0) {
        return dataHeading.trim();
      }

      const labelledBy = sectionRoot.getAttribute('aria-labelledby');
      if (labelledBy) {
        const labelIds = labelledBy.split(/\s+/);
        for (const labelId of labelIds) {
          if (!labelId) continue;
          const labelText = getTextContent(document.getElementById(labelId));
          if (labelText) return labelText;
        }
      }

      const headingNodes = sectionRoot.querySelectorAll(HEADING_SELECTOR);
      for (const headingNode of headingNodes) {
        const headingText = getTextContent(headingNode);
        if (headingText) return headingText;
      }

      const sectionLabel = sectionRoot.getAttribute('aria-label');
      if (typeof sectionLabel === 'string' && sectionLabel.trim().length > 0) {
        return sectionLabel.trim();
      }

      return '';
    }

    function sectionHasCtas(sectionRoot) {
      if (!(sectionRoot instanceof Element)) return false;
      return Boolean(sectionRoot.querySelector(CTA_SELECTOR));
    }

    function isCtaBlock(sectionRoot) {
      return Boolean(getSectionHeading(sectionRoot) && sectionHasCtas(sectionRoot));
    }

    function humanizeSectionType(token) {
      const normalized = String(token || '')
        .trim()
        .toLowerCase()
        .replace(/_/g, '-');

      if (!normalized) return '';
      if (SECTION_TYPE_ALIASES[normalized]) return SECTION_TYPE_ALIASES[normalized];

      const cleaned = normalized.replace(/-section$/, '');
      if (SECTION_TYPE_ALIASES[cleaned]) return SECTION_TYPE_ALIASES[cleaned];

      return cleaned
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    function getClassTokens(element) {
      if (!(element instanceof Element) || typeof element.className !== 'string') return [];
      return element.className.split(/\s+/).filter(Boolean);
    }

    function getSectionTypeToken(sectionRoot) {
      if (!(sectionRoot instanceof Element)) return '';

      const typedNode = sectionRoot.matches('[data-section-type]')
        ? sectionRoot
        : sectionRoot.querySelector('[data-section-type]');
      const dataType = typedNode ? typedNode.getAttribute('data-section-type') : '';
      if (typeof dataType === 'string' && dataType.trim().length > 0) {
        return dataType.trim();
      }

      const classedNodes = [sectionRoot, ...sectionRoot.querySelectorAll('[class]')];
      const scanLimit = Math.min(classedNodes.length, 16);

      for (let index = 0; index < scanLimit; index += 1) {
        const tokens = getClassTokens(classedNodes[index]);

        for (const token of tokens) {
          const blockName = token.split('__')[0];
          if (!blockName || blockName.includes('--')) continue;
          if (GENERIC_CLASS_PATTERN.test(blockName)) continue;
          return blockName;
        }
      }

      return '';
    }

    function getSectionPayload(sectionRoot) {
      return {
        section_heading: getSectionHeading(sectionRoot),
        section_position: getSectionPosition(sectionRoot),
        section_type: humanizeSectionType(getSectionTypeToken(sectionRoot)),
      };
    }

    function getSectionPosition(sectionRoot) {
      const mainContent = getMainContent();
      if (!mainContent) return '';

      const sections = getTopLevelSections(mainContent);
      const position = sections.indexOf(sectionRoot);
      return position === -1 ? '' : String(position + 1);
    }

    function emitCtaBlockRendered(sectionRoot) {
      if (!(sectionRoot instanceof Element) || renderedSections.has(sectionRoot)) return;
      if (!isCtaBlock(sectionRoot)) return;

      const payload = getSectionPayload(sectionRoot);
      if (!payload.section_heading) return;

      renderedSections.add(sectionRoot);
      window.pushWilsonDataLayer({
        event: 'cta_block_rendered',
        section_heading: payload.section_heading,
        section_position: payload.section_position,
        section_type: payload.section_type,
      });
    }

    function getCtaBlockImpressionKey(sectionRoot, payload) {
      const sectionId = sectionRoot.id || '';
      return [sectionId, payload.section_heading, payload.section_position, payload.section_type].join('|');
    }

    function emitCtaBlockImpression(sectionRoot) {
      if (!(sectionRoot instanceof Element) || impressedSections.has(sectionRoot)) return;
      if (!isCtaBlock(sectionRoot)) return;

      const payload = getSectionPayload(sectionRoot);
      if (!payload.section_heading) return;

      const impressionKey = getCtaBlockImpressionKey(sectionRoot, payload);
      if (impressedKeys.has(impressionKey)) return;

      impressedSections.add(sectionRoot);
      impressedKeys.add(impressionKey);
      window.pushWilsonDataLayer({
        event: 'cta_block_impression',
        section_heading: payload.section_heading,
        section_position: payload.section_position,
        section_type: payload.section_type,
      });

      if (intersectionObserver) intersectionObserver.unobserve(sectionRoot);
    }

    function observeCtaBlock(sectionRoot) {
      if (!(sectionRoot instanceof Element) || observedSections.has(sectionRoot)) return;
      if (!supportsIntersectionObserver || !intersectionObserver) return;
      if (!isCtaBlock(sectionRoot)) return;

      observedSections.add(sectionRoot);
      intersectionObserver.observe(sectionRoot);
    }

    function trackCtaBlock(sectionRoot) {
      emitCtaBlockRendered(sectionRoot);
      observeCtaBlock(sectionRoot);
    }

    function scanCtaBlocks(scope) {
      const mainContent = getMainContent();
      if (!mainContent) return;

      if (scope === document || scope === document.documentElement || scope === mainContent) {
        for (const section of getTopLevelSections(mainContent)) {
          trackCtaBlock(section);
        }
        return;
      }

      if (!(scope instanceof Element) || !mainContent.contains(scope)) return;

      const closestSection = scope.matches('.shopify-section') ? scope : scope.closest('.shopify-section');
      if (closestSection) trackCtaBlock(closestSection);

      const nestedSections = scope.querySelectorAll('.shopify-section');
      for (const nestedSection of nestedSections) {
        trackCtaBlock(nestedSection);
      }
    }

    const intersectionObserver = supportsIntersectionObserver
      ? new IntersectionObserver(function (entries) {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            emitCtaBlockImpression(entry.target);
          }
        })
      : null;

    const mutationObserver = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        for (const addedNode of mutation.addedNodes) {
          if (!(addedNode instanceof Element)) continue;
          scanCtaBlocks(addedNode);
        }
      }
    });

    function initCtaBlockTracking() {
      scanCtaBlocks(document);
      mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initCtaBlockTracking);
    } else {
      initCtaBlockTracking();
    }
  })();
